/**
 * Sticky Scroll Grid Section
 * Progressive enhancement with GSAP fallback for browsers without CSS scroll-timeline support
 */

(function() {
  'use strict';

  // Check for native CSS scroll-timeline support
  const hasScrollSupport = CSS.supports(
    '(animation-timeline: view()) and (animation-range: 0 100%)'
  );

  // Initialize sticky scroll grid on a specific wrapper element
  function initStickyScrollGrid(wrapper) {
    if (!wrapper) return;

    // Get configuration from data attributes
    const config = {
      enhanced: wrapper.dataset.enhanced === 'true',
      layers: wrapper.dataset.layers === 'true',
      center: wrapper.dataset.center === 'true',
      stagger: wrapper.dataset.stagger || 'range',
      scrollDuration: parseFloat(wrapper.dataset.scrollDuration || 100) / 100 // Convert percentage to multiplier
    };

    // Add loading state
    wrapper.dataset.loading = 'true';

    // Remove loading state after images load
    const images = wrapper.querySelectorAll('img');
    let loadedCount = 0;
    const totalImages = images.length;

    const checkImagesLoaded = () => {
      loadedCount++;
      if (loadedCount >= totalImages) {
        wrapper.removeAttribute('data-loading');
      }
    };

    images.forEach(img => {
      if (img.complete) {
        checkImagesLoaded();
      } else {
        img.addEventListener('load', checkImagesLoaded);
        img.addEventListener('error', checkImagesLoaded); // Count errors too
      }
    });

    // Log scroll animation support status
    console.log(`Sticky Scroll Grid: CSS scroll-timeline support = ${hasScrollSupport}`);

    // If native support exists and enhanced mode is on, we're done
    if (hasScrollSupport && config.enhanced) {
      console.log('Using native CSS scroll animations');
      return;
    }

    // Otherwise, set up GSAP fallback
    if (!hasScrollSupport && config.enhanced) {
      initGSAPFallback(wrapper, config);
    }
  }

  // GSAP ScrollTrigger fallback
  function initGSAPFallback(wrapper, config) {
    // Check if GSAP is available
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      loadGSAP().then(() => {
        setupGSAPAnimations(wrapper, config);
      }).catch(error => {
        console.error('Failed to load GSAP:', error);
      });
    } else {
      setupGSAPAnimations(wrapper, config);
    }
  }

  // Load GSAP dynamically if needed
  function loadGSAP() {
    return new Promise((resolve, reject) => {
      // Check if GSAP is already loading
      if (window.gsapLoading) {
        window.addEventListener('gsapLoaded', resolve);
        return;
      }

      window.gsapLoading = true;

      // Load GSAP core
      const gsapScript = document.createElement('script');
      gsapScript.src = 'https://cdn.jsdelivr.net/npm/gsap@3.12.0/dist/gsap.min.js';
      gsapScript.onload = () => {
        // Load ScrollTrigger plugin
        const scrollTriggerScript = document.createElement('script');
        scrollTriggerScript.src = 'https://cdn.jsdelivr.net/npm/gsap@3.12.0/dist/ScrollTrigger.min.js';
        scrollTriggerScript.onload = () => {
          gsap.registerPlugin(ScrollTrigger);
          window.gsapLoading = false;
          window.dispatchEvent(new Event('gsapLoaded'));
          resolve();
        };
        scrollTriggerScript.onerror = () => {
          window.gsapLoading = false;
          reject(new Error('Failed to load ScrollTrigger'));
        };
        document.head.appendChild(scrollTriggerScript);
      };
      gsapScript.onerror = () => {
        window.gsapLoading = false;
        reject(new Error('Failed to load GSAP'));
      };
      document.head.appendChild(gsapScript);
    });
  }

  // Set up GSAP animations
  function setupGSAPAnimations(wrapper, config) {
    const mainSection = wrapper.querySelector('.sticky-scroll-grid-section');
    const scalerImage = wrapper.querySelector('.sticky-scroll-scaler-image');
    const layers = wrapper.querySelectorAll('.sticky-scroll-layer');

    if (!mainSection) return;

    // Kill any existing ScrollTriggers for this wrapper
    const triggers = ScrollTrigger.getAll();
    triggers.forEach(trigger => {
      if (trigger.trigger && wrapper.contains(trigger.trigger)) {
        trigger.kill();
      }
    });

    // Center image scaling animation
    if (config.center && scalerImage) {
      // Adjust the scroll range based on duration setting
      // Lower duration = shorter scroll distance, higher duration = longer scroll distance
      const endPosition = 80 + (20 * (1 - config.scrollDuration)); // Ranges from 60% to 100%

      const scalerTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: mainSection,
          start: 'top -10%',
          end: `bottom ${endPosition}%`,
          scrub: true,
          invalidateOnRefresh: true
        }
      });

      // Calculate initial dimensions with minimal padding
      const getInitialDimensions = () => {
        const fontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
        // Use 3rem padding on desktop, 2rem on mobile to match CSS animation
        const isMobile = window.innerWidth <= 768;
        const padding = fontSize * (isMobile ? 2 : 3);

        // Match the CSS animation starting values (almost full viewport)
        return {
          width: window.innerWidth - padding,
          height: window.innerHeight - padding
        };
      };

      const initialDims = getInitialDimensions();

      // Note: We're animating FROM the large size TO the grid cell size
      // So we set the initial state and animate to auto/100%
      // Duration is affected by the scrollDuration multiplier
      scalerTimeline
        .fromTo(scalerImage, {
          width: initialDims.width,
          height: initialDims.height
        }, {
          width: '100%',
          height: '100%',
          ease: 'power2.inOut',
          duration: 1 * config.scrollDuration
        }, 0);

      // Update on window resize
      let resizeTimer;
      const handleResize = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          ScrollTrigger.refresh();
        }, 250);
      };
      window.addEventListener('resize', handleResize);
    }

    // Layer animations
    if (config.layers && layers.length > 0) {
      // Adjust layer animation scroll range based on duration
      const layerEndPosition = 100 - (30 * (1 - config.scrollDuration)); // Ranges from 70% to 100%

      const layersTl = gsap.timeline({
        scrollTrigger: {
          trigger: mainSection,
          start: 'top -40%',
          end: `bottom ${layerEndPosition}%`,
          scrub: true,
          invalidateOnRefresh: true
        }
      });

      // Single layer animation - no stagger needed
      layers.forEach((layer) => {
        // Fade animation
        layersTl.from(layer, {
          opacity: 0,
          ease: 'sine.out',
          duration: 1 * config.scrollDuration
        }, 0);

        // Scale animation
        layersTl.from(layer, {
          scale: 0,
          ease: 'power1.inOut',
          duration: 1 * config.scrollDuration
        }, 0);
      });
    }

    // Refresh ScrollTrigger on Shopify theme editor changes
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:reorder', () => {
        ScrollTrigger.refresh();
      });
    }
  }

  // Global initialization function
  window.initStickyScrollGrid = initStickyScrollGrid;

  // Auto-initialize all sections on page load
  function initAllSections() {
    const wrappers = document.querySelectorAll('.sticky-scroll-grid-wrapper');
    wrappers.forEach(wrapper => {
      initStickyScrollGrid(wrapper);
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllSections);
  } else {
    initAllSections();
  }

  // Re-initialize on Shopify section events (theme editor)
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      const wrapper = event.target.querySelector('.sticky-scroll-grid-wrapper');
      if (wrapper) {
        initStickyScrollGrid(wrapper);
      }
    });

    document.addEventListener('shopify:section:unload', function(event) {
      // Clean up ScrollTriggers for unloaded sections
      if (typeof ScrollTrigger !== 'undefined') {
        const wrapper = event.target.querySelector('.sticky-scroll-grid-wrapper');
        if (wrapper) {
          const triggers = ScrollTrigger.getAll();
          triggers.forEach(trigger => {
            if (trigger.trigger && wrapper.contains(trigger.trigger)) {
              trigger.kill();
            }
          });
        }
      }
    });
  }

  // Performance optimization: Intersection Observer for lazy initialization
  if ('IntersectionObserver' in window) {
    const observerOptions = {
      root: null,
      rootMargin: '200px',
      threshold: 0
    };

    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !entry.target.dataset.initialized) {
          entry.target.dataset.initialized = 'true';
          initStickyScrollGrid(entry.target);
          sectionObserver.unobserve(entry.target);
        }
      });
    }, observerOptions);

    // Observe sections that are far down the page
    document.addEventListener('DOMContentLoaded', () => {
      const wrappers = document.querySelectorAll('.sticky-scroll-grid-wrapper');
      wrappers.forEach((wrapper, index) => {
        // Initialize first section immediately, observe others
        if (index === 0) {
          initStickyScrollGrid(wrapper);
        } else {
          sectionObserver.observe(wrapper);
        }
      });
    });
  }

})();